/**
 * Suppliers, as cards.
 *
 * Two things this screen exists to fix. Contact is structured: name, channel, handle, so
 * "null: directpeptidespanel.com" cannot happen again. And every lead time is measured from
 * PO transitions with its sample size next to it, so a supplier with one delivery says
 * "insufficient data" rather than inventing a p80.
 */
import { Card, EmptyState, Pill, Skeleton } from '@/components';
import { formatPercent } from '@/lib/format';
import { useSuppliers } from './queries';
import './stock.css';

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  signal: 'Signal',
  telegram: 'Telegram',
  wechat: 'WeChat',
  wickr: 'Wickr',
  other: 'Other',
};

export function SuppliersPage() {
  const suppliers = useSuppliers(true);

  if (suppliers.isLoading) return <Skeleton shape="card" />;

  const rows = suppliers.data?.items ?? [];
  if (rows.length === 0) {
    return <EmptyState message="No suppliers yet." />;
  }

  return (
    <div className="stock-page">
      <div className="stock-supplier__grid">
        {rows.map((supplier) => (
          <Card
            key={supplier.supplier_id}
            title={supplier.name}
            action={supplier.is_active ? null : <Pill tone="neutral">inactive</Pill>}
          >
            <dl className="stock-supplier__facts">
              <div>
                <dt>Contact</dt>
                <dd>
                  {supplier.contact.name ?? supplier.contact_name ?? 'not recorded'}
                  {supplier.contact.channel ? (
                    <>
                      {' '}
                      <Pill tone="info">
                        {CHANNEL_LABEL[supplier.contact.channel] ?? supplier.contact.channel}
                      </Pill>
                    </>
                  ) : null}
                  {supplier.contact.handle ? (
                    <div className="o-mono o-muted">{supplier.contact.handle}</div>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Measured lead time</dt>
                <dd>
                  {supplier.lead_insufficient_data ? (
                    <span className="o-muted">
                      Insufficient data, using the {supplier.default_lead_days} day default
                      {supplier.lead_sample_n > 0 ? ` (n=${supplier.lead_sample_n})` : ''}
                    </span>
                  ) : (
                    <>
                      p50 {Math.round(supplier.lead_days_median ?? 0)}d, p80{' '}
                      {Math.round(supplier.lead_days_p80 ?? 0)}d
                      <span className="o-muted"> over {supplier.lead_sample_n} received orders</span>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt>On time</dt>
                <dd>
                  {supplier.on_time_pct === null ? (
                    <span className="o-muted">no purchase order had an expected date</span>
                  ) : (
                    formatPercent(supplier.on_time_pct)
                  )}
                </dd>
              </div>
              <div>
                <dt>Open POs</dt>
                <dd>{supplier.open_po_count}</dd>
              </div>
              <div>
                <dt>Active batches</dt>
                <dd>{supplier.active_batch_count}</dd>
              </div>
              <div>
                <dt>Terms</dt>
                <dd>{supplier.payment_terms ?? 'not recorded'}</dd>
              </div>
              <div>
                <dt>Last ordered</dt>
                <dd>{supplier.last_ordered_at ?? 'never'}</dd>
              </div>
              <div>
                <dt>QC release</dt>
                <dd>
                  {supplier.auto_release_on_coa
                    ? 'Auto release on a COA match'
                    : 'Owner releases the stock'}
                </dd>
              </div>
            </dl>
            {supplier.notes ? <p className="o-muted">{supplier.notes}</p> : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
